/**
 * Indicators pane — spec §F5.2, top→bottom:
 *   1. *Map mode* Seg [Single indicator | Bivariate (two)] (+ the §F5.4 hint note)
 *   2. *Indicator* — header hint, then **selects that name the indicator itself**
 *
 * ## The 2026-08-23 client correction (D1), in this file
 *
 * > *"In the Indicators pane the dropdown selects the ACTUAL INDICATOR (B.2.1 etc.), not the
 * > group … picking maps it as the fill (empty option = nothing mapped) … the per-item
 * > rows/+ buttons go."*
 *
 * C3 had made the three tier accordions one `<select>` **of tiers**, with the chosen tier's
 * `IndItem` rows below it. That is one control plus a list; D1 is one control. The tiers are
 * `<optgroup>`s inside it — ★ Contractual, Outcome, Other, the same order and the same star —
 * so nothing about the §F5.2 grouping is lost, and the row list is gone along with the step
 * it existed to serve.
 *
 * **The dual display is preserved by the second select, not by the rows.** §F5.5's overlays
 * and §F5.4's Y axis were both "click a row, in a mode that changes what a click means" — a
 * control whose meaning is a mode is exactly the thing a reader has to be told about. Now:
 *
 * * **single mode** — *Indicator* (the fill) + *Overlay*, which **adds** on pick and resets
 *   itself to the placeholder. What is overlaid is listed under it, each with the §G4 colour
 *   the map is actually drawing it in and a ✕ that removes it. Two is still the maximum and
 *   still says so (§F5.5's toast), because the cap is the store's and the reason is here;
 * * **bivariate mode** — the same fill select *is* the X axis, and a *Y indicator* select
 *   sits under it. §F5.4's hint note is unchanged and still names both axes.
 *
 * Everything a row used to carry that was not a decoration is still here. A demo-only
 * indicator is a **disabled option marked DEMO** (it stays listed: the catalogue is part of
 * what was commissioned, and an indicator that vanishes reads as a portal that lost it), the
 * refusals keep their toasts for every path that can still reach them, and the auto-behaviours
 * — §F5.2's ramp reset, §M3.5's reporting-level reconciliation — are `setIndicator`'s and are
 * untouched.
 *
 * Data: `['indicators']` (§D3 `GET /indicators`, 27 rows) and `['config']` for the domain
 * palette. The domain rail narrows the **options only** — §F5.2 is explicit that it never
 * touches the map, so a filtered-out mapped indicator stays painted and stays visible in its
 * own select (`PaneSelect`'s `fallback`).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { fetchConfig, fetchIndicators, queryKeys } from '../../lib/api';
import { levelLabelKey, MAX_OVERLAYS, useAppStore, type MapMode } from '../../lib/state';
import { overlayColor } from '../../lib/symbology';
import { toast } from '../../lib/toast';
import { Seg } from '../../components/Seg';
import { useFillRampLastStop } from '../map/overlayClasses';
import { PaneSelect, type PaneOptGroup } from './PaneSelect';
import { groupByTier, TIERS } from './tiers';

export function IndicatorsPane() {
  const { t } = useTranslation();
  const indicator = useAppStore((s) => s.indicator);
  const setIndicator = useAppStore((s) => s.setIndicator);
  const domFilter = useAppStore((s) => s.domFilter);
  const reportingLevel = useAppStore((s) => s.reportingLevel);
  const overlays = useAppStore((s) => s.overlays);
  const addOverlay = useAppStore((s) => s.addOverlay);
  const removeOverlay = useAppStore((s) => s.removeOverlay);
  const bivOn = useAppStore((s) => s.biv.on);
  const bivY = useAppStore((s) => s.biv.y);
  const setMapMode = useAppStore((s) => s.setMapMode);
  const setBivY = useAppStore((s) => s.setBivY);
  const fillRampLastStop = useFillRampLastStop();

  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });

  const domains = useMemo(() => config?.domains ?? [], [config]);
  const domainById = useMemo(() => new Map(domains.map((d) => [d.id, d])), [domains]);
  const byId = useMemo(
    () => new Map((indicators ?? []).map((i) => [i.id, i])),
    [indicators],
  );
  const nameOf = (id: string | null): string => (id ? (byId.get(id)?.name ?? id) : '');

  /**
   * The three §F5.2 tiers as `<optgroup>`s, in TIERS order, narrowed by the domain rail.
   *
   * An empty tier is dropped rather than rendered empty — an `<optgroup>` with no options is
   * a heading for nothing — which is the same rule C3's select applied to its own options.
   */
  const groups: PaneOptGroup[] = useMemo(() => {
    const rows = (indicators ?? []).filter((i) => !domFilter || i.domain === domFilter);
    const grouped = groupByTier(rows);
    return TIERS.filter((tier) => grouped[tier].length > 0).map((tier) => ({
      label: `${tier === 'contractual' ? '★ ' : ''}${t(`indicators.tier.${tier}`)}`,
      options: grouped[tier].map((ind) => ({
        value: ind.id,
        // §F5.2's `title=full_name` had nowhere to go on a row that no longer exists, so the
        // unit rides in the option text — it is what tells two similarly named indicators
        // apart in a list — and the definition stays on the option's own tooltip.
        label: `${ind.name}${ind.active ? '' : ` — ${t('indicators.demoSuffix')}`}`,
        disabled: !ind.active,
        title: ind.active ? (ind.full_name ?? ind.name) : t('indicators.inactiveTitle'),
      })),
    }));
  }, [indicators, domFilter, t]);

  /**
   * §F5.4's "On enter: clear overlays (toast why), keep base as X". The clearance is the
   * store's; the *explanation* is here, because §F5.4 asks for a message and a store action
   * has no `t()`.
   *
   * **Bivariate needs an X to keep.** The mockup fell back to hard-coding `state.indicator
   * = 'b21'`; with the indicator list coming from §D3 that would be a client-side guess at
   * the catalogue, so the mode refuses with a toast instead and the reader maps something
   * first. Logged as a deviation in NOTES.
   */
  const changeMode = (mode: MapMode) => {
    if (mode === 'single') {
      setMapMode('single');
      return;
    }
    if (bivOn) return;
    if (!indicator) {
      toast(t('indicators.bivariateNeedsBase'));
      return;
    }
    const hadOverlays = overlays.length > 0;
    setMapMode('bivariate');
    if (hadOverlays) toast(t('indicators.bivariateOverlaysCleared'));
  };

  /**
   * The fill select — §F5.2's "set/unset base indicator", and §F5.4's X axis while bivariate
   * is on. `''` is the placeholder: nothing mapped, which is a state the pane has always had
   * (a second click on the active row used to reach it).
   *
   * The demo refusal is kept even though the option is `disabled`: `disabled` is the browser's
   * guarantee for a mouse and a keyboard, not for a programmatic `change` — and this is the
   * one place that knows *why* the option is dead, so it is the one place that can say so.
   *
   * **Emptying it while bivariate is on leaves the mode.** §F5.4's X *is* the base indicator,
   * so a bivariate map with nothing mapped has no X to classify and `useBivariateClasses`
   * returns `null` — the reader would be left with the mode still switched on, a Y still
   * chosen, a hint reading "X (→): ." and no fill of either kind on the map. C3 could not
   * reach that state (a row click meant *Y* while the mode was on, so the X was unreachable);
   * D1's own select can, which is why the resolution belongs here. It is the rule
   * `PermalinkSync` already applies to the same fact from the other direction — an indicator a
   * stale link loses takes bivariate mode with it — and it is silent for the same reason: the
   * Seg moves back to *Single* and the hint goes, which is the message.
   */
  const pickFill = (id: string) => {
    if (!id) {
      if (bivOn) setMapMode('single');
      setIndicator(null);
      return;
    }
    const ind = byId.get(id);
    if (!ind) return;
    if (!ind.active) {
      toast(t('indicators.inactiveTitle'));
      return;
    }
    // Two axes of one map cannot be the same indicator: taking the Y as the new X leaves the
    // reader a Y to pick rather than a map that encodes one variable twice.
    if (bivOn && bivY === id) {
      setBivY(null);
      toast(t('indicators.bivariateXtakesY', { name: ind.name }));
    }
    // §M3.5: the catalogue row is right here, so the store can decide in the same tick
    // whether `reportingLevel: 'watershed'` survives the pick (see `setIndicator`).
    setIndicator(id, ind.has_watershed_values);
  };

  /**
   * §F5.4's Y axis, as a select rather than as "a row click, but it means something else now".
   * `''` drops the Y and leaves the mode on, which is exactly the state the hint asks about.
   */
  const pickY = (id: string) => {
    if (!id) {
      setBivY(null);
      return;
    }
    const ind = byId.get(id);
    if (!ind) return;
    if (!ind.active) {
      toast(t('indicators.inactiveTitle'));
      return;
    }
    if (id === indicator) {
      toast(t('indicators.bivariateIsX', { name: ind.name }));
      return;
    }
    setBivY(id);
  };

  /**
   * §F5.5's overlay `+`, as an **add-only** select: the control says "add an overlay", so a
   * pick can only add one, and removing is the ✕ on the list below (which is
   * `removeOverlay` — the same action §F5.6(c)'s legend ✕ calls, so the sidebar and the
   * legend cannot disagree about what is overlaid).
   *
   * The refusals are the mockup's, in its order: a demo-only indicator first (so the reason
   * the reader hears is the real one), then the base indicator, then the cap. Re-picking
   * something already overlaid is silently nothing — it is already there, listed under the
   * select, and telling a reader off for asking for what they already have is noise.
   */
  const pickOverlay = (id: string) => {
    if (!id) return;
    const ind = byId.get(id);
    if (!ind) return;
    if (!ind.active) {
      toast(t('indicators.inactiveTitle'));
      return;
    }
    if (overlays.some((o) => o.id === id)) return;
    if (id === indicator) {
      toast(t('indicators.overlayIsBase'));
      return;
    }
    if (overlays.length >= MAX_OVERLAYS) {
      toast(t('indicators.overlayMax'));
      return;
    }
    addOverlay(id);
    toast(
      t('indicators.overlayAdded', {
        name: ind.name,
        glyph: t(overlays.length === 0 ? 'legend.overlayCircles' : 'legend.overlayRings'),
      }),
    );
  };

  const xName = nameOf(indicator);
  const yName = nameOf(bivY);

  return (
    <div className="sb-pane active" id="pane-inds">
      <div className="sb-section">
        <div className="sb-title">{t('indicators.mapMode')}</div>
        <div id="mapModeSeg">
          <Seg
            ariaLabel={t('indicators.mapMode')}
            value={bivOn ? 'bivariate' : 'single'}
            onChange={changeMode}
            options={[
              { value: 'single', label: t('indicators.modeSingle') },
              {
                value: 'bivariate',
                label: t('indicators.modeBivariate'),
                title: t('indicators.modeBivariateTitle'),
              },
            ]}
          />
        </div>
        {/* §F5.2 item 2: "+ hint note when bivariate (F5.4)" — the mockup's
            `#bivHint.note.info`, which names the X axis and asks for the Y. */}
        {bivOn ? (
          <div id="bivHint" className="note info">
            <b>{t('indicators.bivariateAxisX')}</b> {xName}.
            {bivY ? (
              <>
                <br />
                <b>{t('indicators.bivariateAxisY')}</b> {yName}.{' '}
                {t('indicators.bivariateChangeY')}
              </>
            ) : (
              <> {t('indicators.bivariatePickY')}</>
            )}
          </div>
        ) : null}
      </div>

      <div className="sb-section">
        <div className="sb-title">
          {t('indicators.indicator')}
          <span id="lvlHint" className="lvl-hint">
            {t('indicators.mappedAt', { level: t(levelLabelKey(reportingLevel)) })}
          </span>
        </div>
        <div id="indList">
          <PaneSelect
            id="indSelect"
            label={bivOn ? t('indicators.axisX') : t('indicators.indicator')}
            // In single mode the section heading above already reads *Indicator*; in
            // bivariate the caption is doing real work — it says which axis this is.
            showLabel={bivOn}
            value={indicator ?? ''}
            fallback={xName}
            placeholder={t('indicators.selectNone')}
            groups={groups}
            onChange={pickFill}
            lead={
              <span
                className="dot"
                data-inddot={indicator ?? ''}
                aria-hidden="true"
                style={{
                  background: indicator
                    ? (domainById.get(byId.get(indicator)?.domain ?? '')?.color ?? 'var(--ink-3)')
                    : 'transparent',
                }}
              />
            }
          />

          {bivOn ? (
            <PaneSelect
              id="bivYSelect"
              label={t('indicators.axisY')}
              value={bivY ?? ''}
              fallback={yName}
              placeholder={t('indicators.selectY')}
              groups={groups}
              onChange={pickY}
              lead={
                <span
                  className="dot biv-y-dot"
                  data-bivydot={bivY ?? ''}
                  aria-hidden="true"
                  style={{ background: bivY ? '#6C83B5' : 'transparent' }}
                />
              }
            />
          ) : (
            <>
              <PaneSelect
                id="overlaySelect"
                label={t('indicators.overlay')}
                // Never shows a value: it is an *add* control, so it snaps back to the
                // placeholder after every pick and what was added is the list below.
                value=""
                placeholder={t('indicators.selectOverlay')}
                groups={groups}
                onChange={pickOverlay}
              />
              {overlays.length ? (
                <div id="ovActive" className="ov-active-list">
                  {overlays.map((ov, i) => (
                    <ActiveOverlay
                      key={ov.id}
                      id={ov.id}
                      name={nameOf(ov.id)}
                      color={overlayColor(i, overlays, fillRampLastStop)}
                      glyph={t(i === 0 ? 'legend.overlayCircles' : 'legend.overlayRings')}
                      removeLabel={t('legend.overlayRemove', { name: nameOf(ov.id) })}
                      onRemove={() => removeOverlay(ov.id)}
                    />
                  ))}
                </div>
              ) : null}
            </>
          )}

          <div className="tbl-note">{t('indicators.contractualNote')}</div>
        </div>
      </div>
    </div>
  );
}

interface ActiveOverlayProps {
  id: string;
  name: string;
  /** §G4's resolved colour — the one the markers are actually drawn in (§F5.11). */
  color: string;
  glyph: string;
  removeLabel: string;
  onRemove: () => void;
}

/** One line of "what is overlaid": the §G4 swatch, the name, the glyph, and §F5.6(c)'s ✕. */
function ActiveOverlay({ id, name, color, glyph, removeLabel, onRemove }: ActiveOverlayProps) {
  return (
    <div className="ov-active" data-overlay={id}>
      <span className="ov-swatch" style={{ background: color }} aria-hidden="true" />
      <span className="ov-name">
        {name}
        <small>{glyph}</small>
      </span>
      <button
        type="button"
        className="ov-off"
        data-ovremove={id}
        title={removeLabel}
        aria-label={removeLabel}
        onClick={onRemove}
      >
        ✕
      </button>
    </div>
  );
}

export default IndicatorsPane;
